import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import relativeTime from 'dayjs/plugin/relativeTime';
import type {
  TypeDate,
  TypeTimeOfDay,
  TypeDateTime,
  TypeTimeZone,
} from '@/generated/hooks/bishop/models';
import i18n from '@/shared/i18n';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(relativeTime);

// ============================================================================
// Conventions and Default Behavior
// ============================================================================
/**
 * Default Value Conventions:
 *
 * - 0 vs undefined:
 *   - 0 is treated as a valid value and will be formatted as '00' or preserved as 0
 *   - undefined means "not provided" and will use sensible defaults
 *
 * - Default values for undefined:
 *   - Year: '0000' (for ISO formatting)
 *   - Month/Day: '01' (for ISO formatting)
 *   - Hours/Minutes: '00' (for ISO formatting)
 *
 * - ISO Formatting:
 *   - Preserves 0 values as '00' (e.g., month: 0 → '00')
 *   - undefined becomes defaults (e.g., month: undefined → '01')
 *
 * - Display Formatting:
 *   - 0 values are treated as "not provided" and may be omitted from display
 *   - Functions like formatDateDisplay check for > 0 before displaying components
 */

// ============================================================================
// Constants
// ============================================================================

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// ============================================================================
// Date Format Tokens (for use with formatDate and dayjs)
// ============================================================================
/**
 * Date format tokens for use with `formatDate()` function.
 * Based on dayjs format tokens: https://day.js.org/docs/en/display/format
 *
 * Token Reference:
 * - YYYY: 4-digit year (2025)
 * - YY: 2-digit year (25)
 * - MMMM: Full month name (December)
 * - MMM: Abbreviated month name (Dec)
 * - MM: 2-digit month (01-12)
 * - M: Month number (1-12)
 * - DD: 2-digit day (01-31)
 * - D: Day number (1-31)
 * - dddd: Full weekday name (Monday)
 * - ddd: Abbreviated weekday name (Mon)
 * - HH: 24-hour format (00-23)
 * - hh: 12-hour format (01-12)
 * - h: 12-hour format without padding (1-12)
 * - mm: Minutes (00-59)
 * - ss: Seconds (00-59)
 * - A: AM/PM
 * - a: am/pm
 */
export const DATE_FORMAT_TOKENS = {
  /** Short date: "Dec 9, 2025" */
  SHORT_DATE: 'MMM D, YYYY',
  /** Long date: "December 9, 2025" - full month name */
  LONG_DATE: 'MMMM D, YYYY',
  /** Month + day only: "Dec 9" */
  MONTH_DAY: 'MMM D',
  /** Short date with time: "Dec 9, 3:30 PM" */
  SHORT_DATE_TIME: 'MMM D, h:mm A',
  /** Time of day with seconds: "12:34:27 PM" */
  TIME_WITH_SECONDS: 'h:mm:ss A',
  /** Short date with year separated from time by "at": "Sep 12, 2026 at 7:00 AM" */
  DATE_AT_TIME: 'MMM D, YYYY [at] h:mm A',
  /** Compact date with time: "8/29/25 4:23 PM" - for tight spaces */
  COMPACT_DATE_TIME: 'M/D/YY h:mm A',
  /** Date input format: "01/16/2026" - for date picker inputs */
  DATE_INPUT: 'MM/DD/YYYY',
  /** Year only: "2025" - for deciding whether a range needs to state two */
  YEAR: 'YYYY',
} as const;

/** Type for date format token keys */
export type TDateFormatToken = keyof typeof DATE_FORMAT_TOKENS;

/** Type for date format token values */
export type TDateFormatString = (typeof DATE_FORMAT_TOKENS)[TDateFormatToken];

/**
 * Comprehensive mapping of IANA timezone IDs to their abbreviations
 * Used for displaying human-readable timezone names
 */
const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  // UTC
  UTC: 'UTC',
  'Etc/UTC': 'UTC',
  'Etc/GMT': 'GMT',

  // Americas - North America
  'America/New_York': 'EST/EDT',
  'America/Chicago': 'CST/CDT',
  'America/Denver': 'MST/MDT',
  'America/Los_Angeles': 'PST/PDT',
  'America/Phoenix': 'MST',
  'America/Anchorage': 'AKST/AKDT',
  'America/Honolulu': 'HST',
  'America/Toronto': 'EST/EDT',
  'America/Vancouver': 'PST/PDT',
  'America/Edmonton': 'MST/MDT',
  'America/Winnipeg': 'CST/CDT',
  'America/Halifax': 'AST/ADT',
  'America/St_Johns': 'NST/NDT',
  'America/Mexico_City': 'CST/CDT',
  'America/Cancun': 'EST',
  'America/Monterrey': 'CST/CDT',
  'America/Tijuana': 'PST/PDT',

  // Americas - Central America
  'America/Guatemala': 'CST',
  'America/Belize': 'CST',
  'America/San_Jose': 'CST',
  'America/Managua': 'CST',
  'America/Tegucigalpa': 'CST',
  'America/Panama': 'EST',

  // Americas - Caribbean
  'America/Havana': 'CST/CDT',
  'America/Jamaica': 'EST',
  'America/Port-au-Prince': 'EST/EDT',
  'America/Santo_Domingo': 'AST',
  'America/Puerto_Rico': 'AST',
  'America/Barbados': 'AST',

  // Americas - South America
  'America/Sao_Paulo': 'BRT/BRST',
  'America/Buenos_Aires': 'ART',
  'America/Santiago': 'CLT/CLST',
  'America/Lima': 'PET',
  'America/Bogota': 'COT',
  'America/Caracas': 'VET',
  'America/Montevideo': 'UYT',
  'America/Asuncion': 'PYT/PYST',
  'America/La_Paz': 'BOT',
  'America/Guayaquil': 'ECT',

  // Europe - Western
  'Europe/London': 'GMT/BST',
  'Europe/Dublin': 'GMT/IST',
  'Europe/Lisbon': 'WET/WEST',
  'Atlantic/Reykjavik': 'GMT',
  'Atlantic/Azores': 'AZOT/AZOST',

  // Europe - Central
  'Europe/Paris': 'CET/CEST',
  'Europe/Berlin': 'CET/CEST',
  'Europe/Rome': 'CET/CEST',
  'Europe/Madrid': 'CET/CEST',
  'Europe/Amsterdam': 'CET/CEST',
  'Europe/Brussels': 'CET/CEST',
  'Europe/Vienna': 'CET/CEST',
  'Europe/Zurich': 'CET/CEST',
  'Europe/Prague': 'CET/CEST',
  'Europe/Budapest': 'CET/CEST',
  'Europe/Warsaw': 'CET/CEST',
  'Europe/Stockholm': 'CET/CEST',
  'Europe/Oslo': 'CET/CEST',
  'Europe/Copenhagen': 'CET/CEST',

  // Europe - Eastern
  'Europe/Athens': 'EET/EEST',
  'Europe/Helsinki': 'EET/EEST',
  'Europe/Istanbul': 'TRT',
  'Europe/Kiev': 'EET/EEST',
  'Europe/Bucharest': 'EET/EEST',
  'Europe/Sofia': 'EET/EEST',
  'Europe/Riga': 'EET/EEST',
  'Europe/Tallinn': 'EET/EEST',
  'Europe/Vilnius': 'EET/EEST',

  // Europe - Other
  'Europe/Moscow': 'MSK',
  'Europe/Minsk': 'MSK',
  'Europe/Kaliningrad': 'EET',

  // Asia - Middle East
  'Asia/Dubai': 'GST',
  'Asia/Riyadh': 'AST',
  'Asia/Kuwait': 'AST',
  'Asia/Bahrain': 'AST',
  'Asia/Qatar': 'AST',
  'Asia/Muscat': 'GST',
  'Asia/Jerusalem': 'IST/IDT',
  'Asia/Beirut': 'EET/EEST',
  'Asia/Damascus': 'EET/EEST',
  'Asia/Amman': 'EET/EEST',
  'Asia/Baghdad': 'AST',
  'Asia/Tehran': 'IRST/IRDT',

  // Asia - South Asia
  'Asia/Kolkata': 'IST',
  'Asia/Karachi': 'PKT',
  'Asia/Dhaka': 'BST',
  'Asia/Kathmandu': 'NPT',
  'Asia/Colombo': 'IST',

  // Asia - Southeast Asia
  'Asia/Bangkok': 'ICT',
  'Asia/Ho_Chi_Minh': 'ICT',
  'Asia/Jakarta': 'WIB',
  'Asia/Singapore': 'SGT',
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Manila': 'PHT',
  'Asia/Yangon': 'MMT',
  'Asia/Phnom_Penh': 'ICT',
  'Asia/Vientiane': 'ICT',

  // Asia - East Asia
  'Asia/Shanghai': 'CST',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Taipei': 'CST',
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Macau': 'CST',

  // Asia - Central Asia
  'Asia/Almaty': 'ALMT',
  'Asia/Tashkent': 'UZT',
  'Asia/Ashgabat': 'TMT',
  'Asia/Bishkek': 'KGT',
  'Asia/Dushanbe': 'TJT',

  // Asia - Other
  'Asia/Yekaterinburg': 'YEKT',
  'Asia/Novosibirsk': 'NOVT',
  'Asia/Krasnoyarsk': 'KRAT',
  'Asia/Irkutsk': 'IRKT',
  'Asia/Yakutsk': 'YAKT',
  'Asia/Vladivostok': 'VLAT',
  'Asia/Magadan': 'MAGT',

  // Africa - North
  'Africa/Cairo': 'EET',
  'Africa/Casablanca': 'WET',
  'Africa/Algiers': 'CET',
  'Africa/Tunis': 'CET',
  'Africa/Tripoli': 'EET',

  // Africa - West
  'Africa/Lagos': 'WAT',
  'Africa/Accra': 'GMT',
  'Africa/Dakar': 'GMT',
  'Africa/Abidjan': 'GMT',
  'Africa/Monrovia': 'GMT',

  // Africa - East
  'Africa/Nairobi': 'EAT',
  'Africa/Addis_Ababa': 'EAT',
  'Africa/Dar_es_Salaam': 'EAT',
  'Africa/Kampala': 'EAT',
  'Africa/Khartoum': 'CAT',

  // Africa - South
  'Africa/Johannesburg': 'SAST',
  'Africa/Maputo': 'CAT',
  'Africa/Harare': 'CAT',
  'Africa/Lusaka': 'CAT',
  'Africa/Windhoek': 'CAT',

  // Australia & Pacific
  'Australia/Sydney': 'AEDT/AEST',
  'Australia/Melbourne': 'AEDT/AEST',
  'Australia/Brisbane': 'AEST',
  'Australia/Perth': 'AWST',
  'Australia/Adelaide': 'ACDT/ACST',
  'Australia/Darwin': 'ACST',
  'Australia/Hobart': 'AEDT/AEST',
  'Pacific/Auckland': 'NZDT/NZST',
  'Pacific/Fiji': 'FJT',
  'Pacific/Guam': 'ChST',
  'Pacific/Honolulu': 'HST',
  'Pacific/Tahiti': 'TAHT',
  'Pacific/Port_Moresby': 'PGT',
  'Pacific/Noumea': 'NCT',
  'Pacific/Tongatapu': 'TOT',
  'Pacific/Samoa': 'SST',

  // Atlantic
  'Atlantic/Cape_Verde': 'CVT',
  'Atlantic/Canary': 'WET/WEST',
  'Atlantic/Bermuda': 'AST/ADT',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Converts a number to an ordinal string (1st, 2nd, 3rd, etc.)
 */
const getOrdinalSuffix = (num: number): string => {
  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) {
    return `${num}st`;
  }
  if (j === 2 && k !== 12) {
    return `${num}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${num}rd`;
  }
  return `${num}th`;
};

/**
 * Converts UTC offset string to readable format
 * Example: "-14400s" -> "-04:00"
 */
const formatUtcOffset = (offset: string): string => {
  const match = offset.match(/^(-?\d+)s?$/);
  if (!match) return offset;

  const seconds = parseInt(match[1], 10);
  const absSeconds = Math.abs(seconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);

  const sign = seconds < 0 ? '-' : '+';
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');

  return `${sign}${hoursStr}:${minutesStr}`;
};

/**
 * Gets timezone abbreviation from IANA timezone ID
 * Comprehensive mapping of IANA timezone IDs to their abbreviations
 */
const getTimezoneAbbreviation = (timezoneId: string): string => {
  return TIMEZONE_ABBREVIATIONS[timezoneId] || timezoneId;
};

/**
 * Pluralizes a unit name based on count
 */
const pluralize = (count: number, unit: string): string => {
  return count === 1 ? unit : `${unit}s`;
};

// ============================================================================
// Public Formatting Functions
// ============================================================================

/**
 * Formats a date to a human-readable string
 *
 * Examples:
 * - Full date (year, month, day): "October 30, 2024"
 * - Year-month only: "December 2024"
 * - Month-day only: "July 4th"
 *
 * @param date - The date object to format
 * @returns Human-readable date string
 */
export function formatDateDisplay(date: TypeDate): string {
  const year = date.year ?? 0;
  const month = date.month ?? 0;
  const day = date.day ?? 0;

  // Full date
  if (year > 0 && month > 0 && day > 0) {
    // Use dayjs to create a date and format it
    const dayjsDate = dayjs(
      `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
    );
    if (dayjsDate.isValid()) {
      const monthName = dayjsDate.format('MMMM'); // Full month name
      return `${monthName} ${day}, ${year}`;
    }
    // Fallback if invalid
    const monthName = MONTH_NAMES[month - 1] || month.toString();
    return `${monthName} ${day}, ${year}`;
  }

  // Year-month only (e.g., credit card expiration)
  if (year > 0 && month > 0 && day === 0) {
    const dayjsDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`);
    if (dayjsDate.isValid()) {
      const monthName = dayjsDate.format('MMMM'); // Full month name
      return `${monthName} ${year}`;
    }
    // Fallback if invalid
    const monthName = MONTH_NAMES[month - 1] || month.toString();
    return `${monthName} ${year}`;
  }

  // Month-day only (e.g., anniversary)
  if (year === 0 && month > 0 && day > 0) {
    // Use a reference year for dayjs (2000 is a leap year, good for validation)
    const dayjsDate = dayjs(
      `2000-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
    );
    if (dayjsDate.isValid()) {
      const monthName = dayjsDate.format('MMMM'); // Full month name
      return `${monthName} ${getOrdinalSuffix(day)}`;
    }
    // Fallback if invalid
    const monthName = MONTH_NAMES[month - 1] || month.toString();
    return `${monthName} ${getOrdinalSuffix(day)}`;
  }

  // Fallback to ISO-like format with what's available
  const parts: string[] = [];
  if (year > 0) parts.push(year.toString().padStart(4, '0'));
  if (month > 0) parts.push(month.toString().padStart(2, '0'));
  if (day > 0) parts.push(day.toString().padStart(2, '0'));

  return parts.join('-') || '-';
}

/**
 * Formats a time to a human-readable string
 *
 * Examples:
 * - Without seconds: "2:30 PM"
 * - With seconds: "2:30:45 PM"
 * - With milliseconds: "2:30:45.123 PM"
 *
 * @param time - The time object to format
 * @returns Human-readable time string
 */
export function formatTimeDisplay(time: TypeTimeOfDay): string {
  const hours = time.hours ?? 0;
  const minutes = time.minutes ?? 0;
  const seconds = time.seconds ?? 0;
  const nanos = time.nanos ?? 0;

  // Use dayjs to create a time and format it
  // Create a reference date (2000-01-01) and set the time
  const dayjsTime = dayjs(
    `2000-01-01T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
  );

  if (!dayjsTime.isValid()) {
    // Fallback to manual formatting if invalid
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hours12}:${minutesStr} ${period}`;
  }

  // If no seconds and no nanos, show simple format
  if (seconds === 0 && nanos === 0) {
    return dayjsTime.format('h:mm A');
  }

  // If we have nanos, convert to milliseconds and format
  if (nanos > 0) {
    const milliseconds = Math.floor(nanos / 1000000);
    const msStr = milliseconds.toString().padStart(3, '0');
    return `${dayjsTime.format('h:mm:ss')}.${msStr} ${dayjsTime.format('A')}`;
  }

  // Just seconds
  return dayjsTime.format('h:mm:ss A');
}

/**
 * Formats a datetime to a human-readable string with optional timezone
 *
 * Examples:
 * - Simple: "Oct 30, 2024 2:30 PM"
 * - With timezone: "Oct 30, 2024 2:30 PM EST/EDT"
 * - With UTC offset: "Oct 30, 2024 2:30 PM -04:00"
 *
 * @param datetime - The datetime object to format
 * @returns Human-readable datetime string
 */
export function formatDateTimeDisplay(datetime: TypeDateTime): string {
  const year = datetime.year ?? 0;
  const month = datetime.month ?? 0;
  const day = datetime.day ?? 0;
  const hours = datetime.hours ?? 0;
  const minutes = datetime.minutes ?? 0;
  const seconds = datetime.seconds ?? 0;
  const nanos = datetime.nanos ?? 0;

  // Build ISO-like string for dayjs parsing
  const dateStr = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  const isoStr = `${dateStr}T${timeStr}`;

  let dayjsDateTime = dayjs(isoStr);

  // Handle timezone if available
  if (datetime.time_zone?.id) {
    try {
      dayjsDateTime = dayjs.tz(isoStr, datetime.time_zone.id);
    } catch {
      // If timezone is invalid, use UTC
      dayjsDateTime = dayjsDateTime.utc();
    }
  } else if (datetime.utc_offset) {
    // Parse UTC offset and apply it
    const offsetMatch = datetime.utc_offset.match(/^(-?\d+)s?$/);
    if (offsetMatch) {
      const offsetSeconds = parseInt(offsetMatch[1], 10);
      const offsetHours = Math.floor(Math.abs(offsetSeconds) / 3600);
      const offsetMinutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
      const sign = offsetSeconds < 0 ? '-' : '+';
      const offsetStr = `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
      dayjsDateTime = dayjsDateTime.utcOffset(offsetStr, true);
    }
  }

  if (!dayjsDateTime.isValid()) {
    // Fallback to manual formatting if invalid
    const monthName =
      month > 0 ? MONTH_NAMES_SHORT[month - 1] : month.toString();
    const datePart =
      year > 0 && month > 0 && day > 0
        ? `${monthName} ${day}, ${year}`
        : `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const minutesStr = minutes.toString().padStart(2, '0');
    let timePart = `${hours12}:${minutesStr}`;

    if (seconds > 0 || nanos > 0) {
      const secondsStr = seconds.toString().padStart(2, '0');
      timePart += `:${secondsStr}`;
      if (nanos > 0) {
        const milliseconds = Math.floor(nanos / 1000000);
        const msStr = milliseconds.toString().padStart(3, '0');
        timePart += `.${msStr}`;
      }
    }

    timePart += ` ${period}`;

    let timezonePart = '';
    if (datetime.time_zone?.id) {
      const tzAbbr = getTimezoneAbbreviation(datetime.time_zone.id);
      timezonePart = ` ${tzAbbr}`;
    } else if (datetime.utc_offset) {
      const offset = formatUtcOffset(datetime.utc_offset);
      timezonePart = ` ${offset}`;
    }

    return `${datePart} ${timePart}${timezonePart}`;
  }

  // Format date part using dayjs
  const datePart =
    year > 0 && month > 0 && day > 0
      ? dayjsDateTime.format('MMM D, YYYY')
      : `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

  // Format time part using dayjs
  let timePart = '';
  if (seconds > 0 || nanos > 0) {
    if (nanos > 0) {
      const milliseconds = Math.floor(nanos / 1000000);
      const msStr = milliseconds.toString().padStart(3, '0');
      timePart = `${dayjsDateTime.format('h:mm:ss')}.${msStr} ${dayjsDateTime.format('A')}`;
    } else {
      timePart = dayjsDateTime.format('h:mm:ss A');
    }
  } else {
    timePart = dayjsDateTime.format('h:mm A');
  }

  // Add timezone if available
  let timezonePart = '';
  if (datetime.time_zone?.id) {
    const tzAbbr = getTimezoneAbbreviation(datetime.time_zone.id);
    timezonePart = ` ${tzAbbr}`;
  } else if (datetime.utc_offset) {
    const offset = formatUtcOffset(datetime.utc_offset);
    timezonePart = ` ${offset}`;
  }

  return `${datePart} ${timePart}${timezonePart}`;
}

/**
 * Formats a timezone to a human-readable string
 *
 * Examples:
 * - "EST/EDT" for America/New_York
 * - "UTC" for UTC
 * - Returns original ID if no mapping found
 *
 * @param timezone - The timezone object to format
 * @returns Human-readable timezone string
 */
export function formatTimeZoneDisplay(timezone: TypeTimeZone): string {
  const id = timezone.id ?? '';
  if (!id) return '-';

  return getTimezoneAbbreviation(id);
}

/**
 * Formats a duration string to a human-readable format
 *
 * Examples:
 * - "3600s" -> "1 hour"
 * - "120s" -> "2 minutes"
 * - "86400.500s" -> "1 day 500ms"
 * - "90s" -> "1 minute 30 seconds"
 *
 * @param duration - Duration string in format "Xs" where X is seconds (can be decimal)
 * @returns Human-readable duration string
 */
export function formatDurationDisplay(duration: string): string {
  // Parse the duration string (format: "123.456s" or "123s")
  const match = duration.match(/^(-?\d+\.?\d*)s?$/);
  if (!match) return duration;

  const totalSeconds = parseFloat(match[1]);
  if (isNaN(totalSeconds)) return duration;

  const isNegative = totalSeconds < 0;
  const absSeconds = Math.abs(totalSeconds);

  // Use dayjs duration to parse and break down
  const dayjsDuration = dayjs.duration(absSeconds, 'seconds');

  const days = Math.floor(dayjsDuration.asDays());
  const hours = dayjsDuration.hours();
  const minutes = dayjsDuration.minutes();
  const seconds = dayjsDuration.seconds();
  const milliseconds = dayjsDuration.milliseconds();

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${pluralize(days, 'day')}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${pluralize(hours, 'hour')}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${pluralize(minutes, 'minute')}`);
  }
  if (seconds > 0) {
    parts.push(`${seconds} ${pluralize(seconds, 'second')}`);
  }
  if (milliseconds > 0) {
    parts.push(`${milliseconds}ms`);
  }

  // If nothing, it's 0 seconds
  if (parts.length === 0) {
    return '0 seconds';
  }

  const result = parts.join(' ');
  return isNegative ? `-${result}` : result;
}

/**
 * Formats a duration string to a compact format for table display (e.g., "12 mins", "1 hr 2 mins").
 *
 * @param duration - Duration string in format "Xs" where X is seconds (can be decimal)
 * @returns Compact human-readable duration string
 */
export function formatDurationCompact(duration: string): string {
  const match = duration.match(/^(-?\d+\.?\d*)s?$/);
  if (!match) return duration;

  const totalSeconds = parseFloat(match[1]);
  if (isNaN(totalSeconds) || totalSeconds < 0) return duration;

  const dayjsDuration = dayjs.duration(totalSeconds, 'seconds');
  const days = Math.floor(dayjsDuration.asDays());
  const hours = dayjsDuration.hours();
  const minutes = dayjsDuration.minutes();
  const seconds = dayjsDuration.seconds();

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${pluralize(days, 'day')}`);
  if (hours > 0) parts.push(`${hours} ${pluralize(hours, 'hr')}`);
  if (minutes > 0) parts.push(`${minutes} ${pluralize(minutes, 'min')}`);
  if (seconds > 0 && parts.length === 0)
    parts.push(`${seconds} ${pluralize(seconds, 'sec')}`);

  return parts.length > 0 ? parts.join(' ') : '0 secs';
}

/**
 * Formats a duration in seconds into an ultra-compact human-readable string.
 *
 * Designed for tight UI spaces like run log section cards.
 * Shows at most two units and uses short suffixes without spaces.
 *
 * @param totalSeconds - Duration in seconds (can be fractional)
 * @returns Ultra-compact duration string
 * @example
 * formatDurationUltraCompact(0.3)   // "< 1s"
 * formatDurationUltraCompact(5)     // "5s"
 * formatDurationUltraCompact(90)    // "1m 30s"
 * formatDurationUltraCompact(3660)  // "1h 1m"
 */
export function formatDurationUltraCompact(totalSeconds: number): string {
  if (totalSeconds < 0) return '0s';
  if (totalSeconds < 1) return '< 1s';

  const rounded = Math.floor(totalSeconds);
  const days = Math.floor(rounded / 86400);
  const hours = Math.floor((rounded % 86400) / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Computes the duration between two ISO timestamps and formats it as an ultra-compact string.
 *
 * @param firstTimestamp - ISO timestamp of the start
 * @param lastTimestamp - ISO timestamp of the end
 * @returns Formatted duration string, or null if either timestamp is missing or invalid
 * @example
 * computeTimestampDuration('2024-01-01T00:00:00Z', '2024-01-01T00:01:30Z') // "1m 30s"
 * computeTimestampDuration(undefined, '2024-01-01T00:00:00Z') // null
 */
export function computeTimestampDuration(
  firstTimestamp?: string,
  lastTimestamp?: string,
): string | null {
  if (!firstTimestamp || !lastTimestamp) return null;

  const startMs = dayjs(firstTimestamp).valueOf();
  const endMs = dayjs(lastTimestamp).valueOf();
  if (!startMs || !endMs) return null;

  const durationSeconds = Math.max(0, (endMs - startMs) / 1000);
  return formatDurationUltraCompact(durationSeconds);
}

/**
 * Formats a date to ISO format (YYYY-MM-DD)
 * Used for input/output serialization
 *
 * Note: 0 is treated as a valid value and will be formatted as '00'.
 * undefined values will use defaults ('0000' for year, '01' for month/day).
 *
 * @param date - The date object to format
 * @returns ISO date string (YYYY-MM-DD)
 */
export function formatDateISO(date: TypeDate): string {
  const year =
    date.year !== undefined ? date.year.toString().padStart(4, '0') : '0000';
  const month =
    date.month !== undefined ? date.month.toString().padStart(2, '0') : '01';
  const day =
    date.day !== undefined ? date.day.toString().padStart(2, '0') : '01';
  return `${year}-${month}-${day}`;
}

/**
 * Formats a datetime to ISO format (YYYY-MM-DDTHH:mm)
 * Used for input/output serialization
 *
 * Note: 0 is treated as a valid value and will be formatted as '00'.
 * undefined values will use defaults ('0000' for year, '01' for month/day, '00' for time).
 *
 * @param datetime - The datetime object to format
 * @returns ISO datetime string (YYYY-MM-DDTHH:mm)
 */
export function formatDateTimeISO(datetime: TypeDateTime): string {
  const year =
    datetime.year !== undefined
      ? datetime.year.toString().padStart(4, '0')
      : '0000';
  const month =
    datetime.month !== undefined
      ? datetime.month.toString().padStart(2, '0')
      : '01';
  const day =
    datetime.day !== undefined
      ? datetime.day.toString().padStart(2, '0')
      : '01';
  const hours =
    datetime.hours !== undefined
      ? datetime.hours.toString().padStart(2, '0')
      : '00';
  const minutes =
    datetime.minutes !== undefined
      ? datetime.minutes.toString().padStart(2, '0')
      : '00';
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats a time to ISO format (HH:mm)
 * Used for input/output serialization
 *
 * Note: 0 is treated as a valid value and will be formatted as '00'.
 * undefined values will use defaults ('00' for hours/minutes).
 *
 * @param time - The time object to format
 * @returns ISO time string (HH:mm)
 */
export function formatTimeISO(time: TypeTimeOfDay): string {
  const hours =
    time.hours !== undefined ? time.hours.toString().padStart(2, '0') : '00';
  const minutes =
    time.minutes !== undefined
      ? time.minutes.toString().padStart(2, '0')
      : '00';
  return `${hours}:${minutes}`;
}

/**
 * Formats a timestamp string (ISO 8601) to a human-readable format
 * Used for displaying timestamps from API responses
 *
 * Uses dayjs format strings directly. See https://day.js.org/docs/en/display/format
 * Common format tokens:
 * - Date: YYYY (year), YY (2-digit year), MMMM (month name), MMM (short month), MM (month number), DD (day), dddd (day name), ddd (short day), d (day of week 0-6)
 * - Time: HH (24-hour), hh (12-hour), mm (minutes), ss (seconds), A (AM/PM)
 *
 * @param timestamp - ISO 8601 timestamp string (e.g., "2024-10-30T14:30:00Z")
 * @param format - Format string in dayjs format (default: "MM/DD/YYYY hh:mm A")
 * @returns Formatted date string
 */
export function formatTimestamp(
  timestamp: string,
  format: string = 'MM/DD/YYYY hh:mm A',
): string {
  const dayjsDate = dayjs(timestamp);
  if (!dayjsDate.isValid()) {
    return timestamp; // Return original if invalid
  }

  return dayjsDate.format(format);
}

/**
 * Formats a timestamp explicitly in UTC (not the viewer's local timezone).
 * Use for values that are meaningful in UTC regardless of who is looking — e.g.
 * an appliance's auto-stop time. Returns null for a missing/unparseable input so
 * callers can fall back.
 *
 * @param timestamp - ISO-8601 (or any dayjs-parseable) timestamp
 * @param format - dayjs format string; default appends a literal " UTC"
 */
export function formatTimestampUtc(
  timestamp: string | undefined,
  format: string = 'MMM D, YYYY, h:mm A [UTC]',
): string | null {
  if (!timestamp) return null;
  const dayjsDate = dayjs.utc(timestamp);
  if (!dayjsDate.isValid()) return null;
  return dayjsDate.format(format);
}

/**
 * Formats an epoch-milliseconds timestamp as an ISO-8601 UTC string.
 *
 * For values going back OUT to an API rather than onto the screen — an AIP-160
 * filter bound, a query parameter — where the wire format is fixed and a locale
 * format would be wrong.
 *
 * @param epochMs - Milliseconds since epoch
 * @returns ISO-8601 UTC string, or an empty string if invalid
 */
export function formatEpochMsISO(epochMs: number): string {
  const dayjsDate = dayjs.utc(epochMs);
  if (!dayjsDate.isValid()) {
    return '';
  }

  return dayjsDate.toISOString();
}

/**
 * Formats an epoch-milliseconds timestamp (e.g. `V1Event.recorded_at_ms`) to
 * a human-readable format, without callers needing their own `new Date(ms)`.
 *
 * Uses dayjs format strings directly. See https://day.js.org/docs/en/display/format
 *
 * @param epochMs - Milliseconds since epoch
 * @param format - Format string in dayjs format (default: "MM/DD/YYYY hh:mm A")
 * @returns Formatted date string, or an empty string if invalid
 */
export function formatEpochMs(
  epochMs: number,
  format: string = 'MM/DD/YYYY hh:mm A',
): string {
  const dayjsDate = dayjs(epochMs);
  if (!dayjsDate.isValid()) {
    return '';
  }

  return dayjsDate.format(format);
}

/**
 * Formats a Date object or timestamp string to locale string
 * Used for displaying dates in a locale-aware format
 *
 * @param date - Date object, timestamp string, or undefined
 * @returns Formatted date string or empty string
 */
export function formatDateLocale(date: string | Date | undefined): string {
  if (!date) return '';
  if (typeof date === 'string') {
    const dayjsDate = dayjs(date);
    if (dayjsDate.isValid()) {
      return dayjsDate.format('M/D/YYYY, h:mm:ss A');
    }
    return date;
  }
  // Date object
  const dayjsDate = dayjs(date);
  if (dayjsDate.isValid()) {
    return dayjsDate.format('M/D/YYYY, h:mm:ss A');
  }
  return '';
}

/**
 * Parses a date string (YYYY-MM-DD) to TypeDate
 * Used for converting ISO date strings to TypeDate objects
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns TypeDate object
 * @throws {Error} If the date string format is invalid or contains invalid values
 */
export function parseDate(dateStr: string): TypeDate {
  // Validate format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    throw new Error(
      `Invalid date format: "${dateStr}". Expected format: YYYY-MM-DD`,
    );
  }

  const parts = dateStr.split('-');
  const year = parseInt(parts[0]!, 10);
  const month = parseInt(parts[1]!, 10);
  const day = parseInt(parts[2]!, 10);

  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error(
      `Invalid date values in: "${dateStr}". All components must be valid numbers.`,
    );
  }

  return { year, month, day };
}

/**
 * Parses a datetime string (YYYY-MM-DDTHH:mm) to TypeDateTime
 * Used for converting ISO datetime strings to TypeDateTime objects
 *
 * @param datetimeStr - Datetime string in YYYY-MM-DDTHH:mm format
 * @returns TypeDateTime object
 * @throws {Error} If the datetime string format is invalid or contains invalid values
 */
export function parseDateTime(datetimeStr: string): TypeDateTime {
  // Validate format
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(datetimeStr)) {
    throw new Error(
      `Invalid datetime format: "${datetimeStr}". Expected format: YYYY-MM-DDTHH:mm`,
    );
  }

  const [datePart, timePart] = datetimeStr.split('T');
  if (!datePart || !timePart) {
    throw new Error(
      `Invalid datetime format: "${datetimeStr}". Missing date or time component.`,
    );
  }

  const dateParts = datePart.split('-');
  const timeParts = timePart.split(':');

  if (dateParts.length !== 3 || timeParts.length !== 2) {
    throw new Error(
      `Invalid datetime format: "${datetimeStr}". Date must have 3 parts, time must have 2 parts.`,
    );
  }

  const year = parseInt(dateParts[0]!, 10);
  const month = parseInt(dateParts[1]!, 10);
  const day = parseInt(dateParts[2]!, 10);
  const hours = parseInt(timeParts[0]!, 10);
  const minutes = parseInt(timeParts[1]!, 10);

  // Validate parsed values
  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    isNaN(hours) ||
    isNaN(minutes)
  ) {
    throw new Error(
      `Invalid datetime values in: "${datetimeStr}". All components must be valid numbers.`,
    );
  }

  // Validate ranges
  if (hours < 0 || hours > 23) {
    throw new Error(
      `Invalid hours value: ${hours}. Hours must be between 0 and 23.`,
    );
  }
  if (minutes < 0 || minutes > 59) {
    throw new Error(
      `Invalid minutes value: ${minutes}. Minutes must be between 0 and 59.`,
    );
  }

  return { year, month, day, hours, minutes, seconds: 0, nanos: 0 };
}

/**
 * Parses a time string (HH:mm) to TypeTimeOfDay
 * Used for converting time strings to TypeTimeOfDay objects
 *
 * @param timeStr - Time string in HH:mm format
 * @returns TypeTimeOfDay object
 * @throws {Error} If the time string format is invalid or contains invalid values
 */
export function parseTime(timeStr: string): TypeTimeOfDay {
  // Validate format
  if (!/^\d{2}:\d{2}$/.test(timeStr)) {
    throw new Error(
      `Invalid time format: "${timeStr}". Expected format: HH:mm`,
    );
  }

  const parts = timeStr.split(':');
  if (parts.length !== 2) {
    throw new Error(
      `Invalid time format: "${timeStr}". Time must have 2 parts (hours:minutes).`,
    );
  }

  const hours = parseInt(parts[0]!, 10);
  const minutes = parseInt(parts[1]!, 10);

  // Validate parsed values
  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error(
      `Invalid time values in: "${timeStr}". Hours and minutes must be valid numbers.`,
    );
  }

  // Validate ranges
  if (hours < 0 || hours > 23) {
    throw new Error(
      `Invalid hours value: ${hours}. Hours must be between 0 and 23.`,
    );
  }
  if (minutes < 0 || minutes > 59) {
    throw new Error(
      `Invalid minutes value: ${minutes}. Minutes must be between 0 and 59.`,
    );
  }

  return { hours, minutes, seconds: 0, nanos: 0 };
}

/**
 * Validates if a date string is a valid date
 * Used for validation purposes (e.g., in Zod schemas)
 * This function is lenient - dayjs wraps invalid dates (e.g., '2024-13-45' becomes '2025-02-14')
 *
 * @param dateStr - Date string to validate
 * @returns true if valid, false otherwise
 */
export function isValidDate(dateStr: string): boolean {
  const dayjsDate = dayjs(dateStr);
  return dayjsDate.isValid();
}

/**
 * Strictly validates if a date string represents a valid date without wrapping
 * Used for input validation where we want to reject invalid dates like '2024-13-45'
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns true if valid and matches the input exactly, false otherwise
 */
export function isValidDateStrict(dateStr: string): boolean {
  // Parse the date string
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const [, yearStr, monthStr, dayStr] = match;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  // Check if month and day are in valid ranges
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Handle year 0 edge case - JavaScript Date doesn't handle year 0 well
  // For year 0, we'll manually validate based on month/day ranges
  if (year === 0) {
    // Check if day is valid for the given month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
  }

  // Create a date object and check if it matches the input
  // This will catch invalid dates like Feb 30, Apr 31, etc.
  const date = new Date(year, month - 1, day);

  // Check if the date components match (this catches invalid dates that get wrapped)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  return true;
}

/**
 * Strictly validates if a datetime string represents a valid datetime without wrapping
 * Used for input validation where we want to reject invalid datetimes like '2024-13-45T25:99'
 *
 * @param datetimeStr - Datetime string in YYYY-MM-DDTHH:mm format
 * @returns true if valid and matches the input exactly, false otherwise
 */
export function isValidDateTimeStrict(datetimeStr: string): boolean {
  // Parse the datetime string
  const match = datetimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return false;

  const [, yearStr, monthStr, dayStr, hoursStr, minutesStr] = match;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Check if date components are valid
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Check if time components are valid
  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;

  // Handle year 0 edge case - JavaScript Date doesn't handle year 0 well
  // For year 0, we'll manually validate based on month/day ranges
  if (year === 0) {
    // Check if day is valid for the given month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
  }

  // Create a date object and check if it matches the input
  const date = new Date(year, month - 1, day, hours, minutes);

  // Check if the date components match (this catches invalid dates that get wrapped)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hours ||
    date.getMinutes() !== minutes
  ) {
    return false;
  }

  return true;
}

/**
 * Strictly validates if a time string represents a valid "HH:mm" 24-hour time.
 *
 * A native `<input type="time">` reports an empty (or, mid-edit, partial) value
 * while the user types directly into it rather than using the up/down arrows.
 * This guards against those transient values being treated as complete times.
 *
 * @param timeStr - Time string in HH:mm format (may be empty/partial/undefined)
 * @returns true if `timeStr` is a valid "HH:mm" string, false otherwise
 */
export function isValidTimeStrict(
  timeStr: string | undefined,
): timeStr is string {
  if (!timeStr) return false;

  const match = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;

  const [, hoursStr, minutesStr] = match;
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Check if time components are in valid ranges
  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;

  return true;
}

/**
 * Formats an ISO timestamp string into a human-readable relative time string.
 * e.g. "a few seconds ago", "2 minutes ago", "3 hours ago"
 *
 * @param timestamp - ISO 8601 timestamp string (e.g., "2024-01-15T14:30:00Z")
 * @returns Relative time string
 */
export function formatRelativeTime(timestamp: string): string {
  return dayjs(timestamp).fromNow();
}

/**
 * Age with no "ago" on the end — "2 hours", "20 minutes".
 *
 * For composing a SPAN, where one suffix has to cover both ends: "2 hours – 20
 * minutes ago" reads, and "2 hours ago – 20 minutes ago" does not.
 *
 * @param timestamp - ISO 8601 timestamp string
 * @returns Relative age without a suffix
 */
export function formatRelativeAge(timestamp: string): string {
  return dayjs(timestamp).fromNow(true);
}

/**
 * Formats the most recent of the given timestamps into a short activity label:
 * - under 2 minutes        → "Just now" (skips the singular "1 min ago")
 * - within 10 minutes      → "{n} mins ago"
 * - within 24 hours        → time of day ("10:25 PM")
 * - within a week          → relative ("2 days ago")
 * - a week or older        → absolute date ("July 24, 2026")
 *
 * Returns '' when no valid timestamp is provided.
 *
 * @param timestamps - ISO 8601 timestamp strings (e.g. update_time, create_time)
 * @returns Formatted activity label
 */
export function formatActivityTimestamp(
  ...timestamps: Array<string | undefined>
): string {
  const mostRecent = timestamps
    .filter((timestamp): timestamp is string => Boolean(timestamp))
    .map(timestamp => dayjs(timestamp))
    .filter(date => date.isValid())
    .sort((a, b) => b.valueOf() - a.valueOf())[0];

  if (!mostRecent) {
    return '';
  }

  const now = dayjs();
  const minutesAgo = now.diff(mostRecent, 'minute');
  if (minutesAgo < 2) {
    return i18n.t('common.justNow', { ns: 'common' });
  }
  if (minutesAgo <= 10) {
    return i18n.t('common.minutesAgo', { ns: 'common', count: minutesAgo });
  }

  if (now.diff(mostRecent, 'hour') < 24) {
    return mostRecent.format('h:mm A');
  }

  if (now.diff(mostRecent, 'day') < 7) {
    return mostRecent.fromNow();
  }

  return mostRecent.format('MMMM D, YYYY');
}

/**
 * A timestamp short enough for a narrow column: the time today, the day
 * earlier this year, and the full date before that.
 *
 * @param timestamp - ISO timestamp
 * @returns e.g. "9:42 PM", "Oct 5", or "Oct 5, 2025"
 */
export function formatCompactTimestamp(timestamp: string): string {
  const at = dayjs(timestamp);
  if (!at.isValid()) return '';
  const now = dayjs();
  if (at.isSame(now, 'day')) return at.format('h:mm A');
  return at.format(at.isSame(now, 'year') ? 'MMM D' : 'MMM D, YYYY');
}

/**
 * Checks if a timestamp is older than the specified number of days from now
 *
 * @param timestamp - ISO 8601 timestamp string
 * @param days - Number of days threshold
 * @returns true if the timestamp is older than the specified days
 */
export function isOlderThanDays(timestamp: string, days: number): boolean {
  return dayjs().diff(dayjs(timestamp), 'day') > days;
}

/**
 * Returns true when the timestamp falls in the current calendar year
 * (viewer's local timezone, matching `formatTimestamp`).
 */
export function isInCurrentYear(timestamp: string): boolean {
  return dayjs(timestamp).year() === dayjs().year();
}

/**
 * Gets the current timestamp as a Date object
 * Used when you need a Date object for timestamps (e.g., in stores, messages)
 *
 * @returns Current Date object
 */
export function getCurrentDate(): Date {
  return new Date();
}

/**
 * Gets the date one week ago from today as a Date object
 * Used when you need a Date object for date ranges (e.g., filtering data from the past week)
 *
 * @returns Date object representing one week ago
 */
export function getDateOneWeekAgo(): Date {
  return dayjs().subtract(1, 'week').toDate();
}

/**
 * Creates a Date object from year, month, and day components.
 *
 * @param year - The year
 * @param month - The month (0-indexed, 0=January)
 * @param day - The day of month
 * @returns Date object for the specified date
 */
export function createDate(year: number, month: number, day: number): Date {
  return new Date(year, month, day);
}

/**
 * Converts a JS `Date` to a `TypeDate` (google.type.Date), whose `month` is
 * 1-based. The inverse direction of {@link createDate}.
 *
 * @param date - The JS Date to convert
 * @returns TypeDate with `year`, 1-based `month`, and `day`
 */
export function dateToTypeDate(date: Date): TypeDate {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

/**
 * Gets the start of the current week (Monday) as a Date object
 * Used when you need a Date object for week-based date ranges
 *
 * @returns Date object representing the start of the current week (Monday)
 */
export function getStartOfWeek(): Date {
  return dayjs().startOf('isoWeek').toDate();
}

/**
 * Gets the first day of the month for a given date.
 * Used when you need a Date object for month-based date ranges.
 *
 * @param date - The reference date
 * @returns Date object set to the first day of the month at midnight
 */
export function getStartOfMonth(date: Date): Date {
  return dayjs(date).startOf('month').toDate();
}

/**
 * Converts a Date object, timestamp string, or undefined to a numeric timestamp.
 * Used for sorting and comparing timestamps when the value may be in different formats.
 *
 * @param timestamp - Date object, ISO timestamp string, or undefined
 * @returns Numeric timestamp in milliseconds (0 if undefined or invalid)
 *
 * @example
 * getTimestamp(new Date('2024-01-15')) // Returns: 1705276800000
 * getTimestamp('2024-01-15T14:30:00Z') // Returns: 1705336200000
 * getTimestamp(undefined) // Returns: 0
 */
export function getTimestamp(timestamp: Date | string | undefined): number {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  return Date.parse(timestamp) || 0;
}

/**
 * Parses an ISO 8601 timestamp string to a Date object.
 * Used for converting API response timestamps (ISO strings) to Date objects.
 *
 * @param timestamp - ISO 8601 timestamp string (e.g., "2024-01-15T14:30:00Z")
 * @returns Date object parsed from the timestamp string
 *
 * @example
 * parseTimestamp('2024-01-15T14:30:00Z') // Returns Date object
 */
export function parseTimestamp(timestamp: string): Date {
  return new Date(timestamp);
}

/**
 * Converts milliseconds since the epoch to a Date object.
 * For wire fields carrying epoch millis rather than an ISO string
 * (e.g. Rain's `V1Event.recorded_at_ms`).
 *
 * @param epochMs - Milliseconds since epoch
 * @returns Date object at that instant
 *
 * @example
 * epochMsToDate(1700000000000) // Returns Date object
 */
export function epochMsToDate(epochMs: number): Date {
  return new Date(epochMs);
}

/**
 * Formats a Date object to a string using the specified format
 * Used for formatting Date objects directly (e.g., in date pickers)
 *
 * Uses dayjs format strings directly. See https://day.js.org/docs/en/display/format
 * Common format tokens:
 * - Date: YYYY (year), YY (2-digit year), MMMM (month name), MMM (short month), MM (month number), DD (day), dddd (day name), ddd (short day), d (day of week 0-6)
 * - Time: HH (24-hour), hh (12-hour), mm (minutes), ss (seconds), A (AM/PM)
 *
 * @param date - Date object to format
 * @param format - Format string in dayjs format (default: "MMMM D, YYYY")
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | undefined,
  format: string = 'MMMM D, YYYY',
): string {
  if (!date) return '';
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return '';
  }

  return dayjsDate.format(format);
}

/**
 * Formats a date with relative labels for today and yesterday.
 * Returns "Today", "Yesterday", or a formatted date string without time.
 *
 * @param date - Date object to format
 * @returns "Today", "Yesterday", or formatted date string (e.g., "Dec 9, 2025")
 *
 * @example
 * formatRelativeDate(new Date()); // "Today"
 * formatRelativeDate(addDays(new Date(), -1)); // "Yesterday"
 * formatRelativeDate(addDays(new Date(), -5)); // "Dec 9, 2025"
 */
export function formatRelativeDate(date: Date | undefined): string {
  if (!date) return '';

  const now = getCurrentDate();
  const todayKey = dateToISOString(now);
  const yesterdayKey = dateToISOString(addDays(now, -1));
  const dateKey = dateToISOString(date);

  if (dateKey === todayKey) {
    return i18n.t('common.today', { ns: 'common' });
  }

  if (dateKey === yesterdayKey) {
    return i18n.t('common.yesterday', { ns: 'common' });
  }

  return formatDate(date, DATE_FORMAT_TOKENS.SHORT_DATE);
}

/**
 * Formats a date with relative labels for today and yesterday, including time.
 * Returns "Today", "Yesterday", or a formatted date string with time.
 *
 * @param date - Date object to format
 * @returns "Today", "Yesterday", or formatted date string with time (e.g., "Dec 9, 3:30 PM")
 *
 * @example
 * formatRelativeDateWithTime(new Date()); // "Today"
 * formatRelativeDateWithTime(addDays(new Date(), -1)); // "Yesterday"
 * formatRelativeDateWithTime(addDays(new Date(), -5)); // "Dec 9, 3:30 PM"
 */
export function formatRelativeDateWithTime(date: Date | undefined): string {
  if (!date) return '';

  const now = getCurrentDate();
  const todayKey = dateToISOString(now);
  const yesterdayKey = dateToISOString(addDays(now, -1));
  const dateKey = dateToISOString(date);

  if (dateKey === todayKey) {
    return i18n.t('common.today', { ns: 'common' });
  }

  if (dateKey === yesterdayKey) {
    return i18n.t('common.yesterday', { ns: 'common' });
  }

  return formatDate(date, DATE_FORMAT_TOKENS.SHORT_DATE_TIME);
}

/**
 * Parses a date string (YYYY-MM-DD) to a Date object.
 * Used for converting ISO date strings to Date objects for date calculations.
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object representing the parsed date
 */
export function parseDateString(dateStr: string): Date {
  return dayjs(dateStr).toDate();
}

/**
 * Normalizes a date to the start of the day (midnight) for accurate date-only comparisons.
 * Used when comparing dates without considering time components.
 *
 * @param date - Date to normalize
 * @param timezone - Optional IANA timezone ID (e.g., 'America/New_York', 'UTC'). If provided, the date will be normalized in that timezone.
 * @returns New Date object set to midnight (00:00:00) of the same day in the specified timezone
 */
export function normalizeToStartOfDay(date: Date, timezone?: string): Date {
  let dayjsDate = dayjs(date);
  if (timezone) {
    dayjsDate = dayjsDate.tz(timezone);
  }
  return dayjsDate.startOf('day').toDate();
}

/**
 * Normalizes a date to the end of the day (23:59:59.999) for accurate date range filtering.
 * Used when filtering data to include all records from a specific day.
 *
 * @param date - Date to normalize
 * @param timezone - Optional IANA timezone ID (e.g., 'America/New_York', 'UTC'). If provided, the date will be normalized in that timezone.
 * @returns New Date object set to end of day (23:59:59.999) of the same day in the specified timezone
 */
export function normalizeToEndOfDay(date: Date, timezone?: string): Date {
  let dayjsDate = dayjs(date);
  if (timezone) {
    dayjsDate = dayjsDate.tz(timezone);
  }
  return dayjsDate.endOf('day').toDate();
}

/**
 * Builds the absolute Date for `time` (HH:mm) on the calendar day of `date`,
 * interpreted in `timezone`. Use when comparing a zoned wall-clock occurrence
 * (e.g. "10:00 in America/Los_Angeles" on a given day) against an instant.
 *
 * @param date - Source date; only its Y-M-D components are used (in local zone)
 * @param time - Wall-clock time as "HH:mm"
 * @param timezone - IANA timezone ID the wall-clock is in
 * @returns Absolute Date for that zoned wall-clock instant
 */
export function getZonedDatetime(
  date: Date,
  time: string,
  timezone: string,
): Date {
  const ymd = dayjs(date).format('YYYY-MM-DD');
  return dayjs.tz(`${ymd} ${time}`, timezone).toDate();
}

/**
 * Calculates the number of days between two dates.
 * Used for date range calculations and grouping logic.
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Number of days between the two dates
 */
export function getDaysDifference(date1: Date, date2: Date): number {
  return Math.floor(
    (date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24),
  );
}

/**
 * Adds a specified number of days to a date.
 * Used for calculating date ranges and group boundaries.
 *
 * @param date - Base date
 * @param days - Number of days to add (can be negative to subtract)
 * @returns New Date object with days added
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Gets the date range for a grouped period based on a reference date and grouping interval.
 * Used for grouping daily data into 5-day or 2-week periods.
 *
 * @param entryDate - The date to find the group for
 * @param periodStartDate - The start date of the overall period
 * @param groupSizeDays - Size of each group in days (e.g., 5 for 5-day groups, 14 for 2-week groups)
 * @returns Object with startDate and endDate as Date objects for the group
 */
export function getDateGroupRange(
  entryDate: Date,
  periodStartDate: Date,
  groupSizeDays: number,
): { startDate: Date; endDate: Date } {
  const daysSinceStart = getDaysDifference(entryDate, periodStartDate);
  const groupIndex = Math.floor(daysSinceStart / groupSizeDays);
  const groupStartDate = addDays(periodStartDate, groupIndex * groupSizeDays);
  const groupEndDate = addDays(groupStartDate, groupSizeDays - 1);
  return { startDate: groupStartDate, endDate: groupEndDate };
}

/**
 * Formats a date range label for display.
 * Used for x-axis labels in charts showing grouped date ranges.
 *
 * @param startDateStr - Start date string in YYYY-MM-DD format
 * @param endDateStr - End date string in YYYY-MM-DD format
 * @returns Formatted date range string (e.g., "Jan 1 - Jan 5" or "Jan 1 - 5")
 */
export function formatDateRangeLabel(
  startDateStr: string,
  endDateStr: string,
): string {
  const start = parseDateString(startDateStr);
  const end = parseDateString(endDateStr);
  const startFormatted = formatTimestamp(startDateStr, 'MMM D');
  const endFormatted = formatTimestamp(endDateStr, 'MMM D');

  // If same month, show "MMM D - D" format, otherwise "MMM D - MMM D"
  if (start.getMonth() === end.getMonth()) {
    const endDay = formatTimestamp(endDateStr, 'D');
    return `${startFormatted} - ${endDay}`;
  }
  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Converts a Date object to an ISO date string (YYYY-MM-DD).
 * Used for converting Date objects back to string format for storage or comparison.
 *
 * @param date - Date object to convert
 * @returns ISO date string in YYYY-MM-DD format
 */
export function dateToISOString(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Splits an ISO datetime string into its date and time halves, for forms that
 * store one combined value but render separate date and time inputs.
 *
 * @param value - 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm', or 'THH:mm'
 * @returns The date ('YYYY-MM-DD') and time ('HH:mm') halves; each is '' when absent
 *
 * @example
 * splitDateTimeISO('2025-08-29T14:30') // { date: '2025-08-29', time: '14:30' }
 * splitDateTimeISO('2025-08-29')       // { date: '2025-08-29', time: '' }
 * splitDateTimeISO('T14:30')           // { date: '', time: '14:30' }
 */
export function splitDateTimeISO(value: string): {
  date: string;
  time: string;
} {
  const [date = '', time = ''] = value.split('T');
  return { date, time };
}

/**
 * Joins date and time halves into a single ISO datetime string. Inverse of
 * `splitDateTimeISO`.
 *
 * @param date - Date half ('YYYY-MM-DD'), or '' when unset
 * @param time - Time half ('HH:mm'), or '' when unset
 * @returns The combined value, or '' when both halves are empty
 *
 * @example
 * joinDateTimeISO('2025-08-29', '14:30') // "2025-08-29T14:30"
 * joinDateTimeISO('2025-08-29', '')      // "2025-08-29"
 * joinDateTimeISO('', '14:30')           // "T14:30"
 */
export function joinDateTimeISO(date: string, time: string): string {
  if (!time) return date;
  if (!date) return `T${time}`;
  return `${date}T${time}`;
}

/**
 * Splits an ISO date interval into its start and end dates, for range pickers
 * that store one combined value.
 *
 * @param value - 'YYYY-MM-DD/YYYY-MM-DD', or a half-picked 'YYYY-MM-DD/'
 * @returns The start and end dates ('YYYY-MM-DD'); each is '' when absent
 *
 * @example
 * splitDateRangeISO('2025-08-01/2025-08-29') // { from: '2025-08-01', to: '2025-08-29' }
 * splitDateRangeISO('2025-08-01/')           // { from: '2025-08-01', to: '' }
 */
export function splitDateRangeISO(value: string): {
  from: string;
  to: string;
} {
  const [from = '', to = ''] = value.split('/');
  return { from, to };
}

/**
 * Joins start and end dates into a single ISO date interval. Inverse of
 * `splitDateRangeISO`.
 *
 * @param from - Start date ('YYYY-MM-DD'), or '' when unset
 * @param to - End date ('YYYY-MM-DD'), or '' when unset
 * @returns The combined value, or '' when both ends are empty
 *
 * @example
 * joinDateRangeISO('2025-08-01', '2025-08-29') // "2025-08-01/2025-08-29"
 * joinDateRangeISO('2025-08-01', '')           // "2025-08-01/"
 */
export function joinDateRangeISO(from: string, to: string): string {
  if (!from && !to) return '';
  return `${from}/${to}`;
}

/**
 * Formats a JavaScript Date for display using a dayjs format string.
 *
 * @param date - The date to format
 * @param format - dayjs format string (defaults to "MMM D, YYYY")
 * @returns Formatted date string
 *
 * @example
 * formatJsDate(new Date('2025-08-29')) // "Aug 29, 2025"
 */
export function formatJsDate(date: Date, format = 'MMM D, YYYY'): string {
  return dayjs(date).format(format);
}

/**
 * Formats seconds as hours, showing one decimal place only if not a whole number.
 * Used for displaying time durations in a compact hour format.
 *
 * @param seconds - Number of seconds (can be number or string)
 * @returns Formatted string with hours (e.g., "1h", "2.5h")
 *
 * @example
 * formatSecondsAsHours(3600) // "1h"
 * formatSecondsAsHours(5400) // "1.5h"
 * formatSecondsAsHours("7200") // "2h"
 */
export function formatSecondsAsHours(seconds: number | string): string {
  const numValue = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
  if (isNaN(numValue)) return '0h';

  const hours = numValue / 3600;
  const roundedHours = Math.round(hours * 10) / 10;
  const formatted = Number.isInteger(roundedHours)
    ? roundedHours.toString()
    : roundedHours.toFixed(1);
  return `${formatted}h`;
}

/**
 * Formats seconds as minutes (whole number).
 *
 * @param seconds - Number of seconds (can be number or string)
 * @returns Formatted string with rounded minutes (e.g., "45m")
 *
 * @example
 * formatSecondsAsMinutes(60) // "1m"
 * formatSecondsAsMinutes(150) // "3m"
 * formatSecondsAsMinutes("3600") // "60m"
 */
export function formatSecondsAsMinutes(seconds: number | string): string {
  const numValue = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
  if (isNaN(numValue)) return '0m';

  const minutes = numValue / 60;
  return `${Math.round(minutes)}m`;
}

/**
 * Checks if a date is in the first or last week of the month.
 *
 * First week: days 1-7
 * Last week: last 7 days of the month
 *
 * @param date - The date to check
 * @returns True if the date is in the first week (days 1-7) or last week of the month
 */
export function isInFirstOrLastWeekOfMonth(date: Date): boolean {
  const d = dayjs(date);
  const dayOfMonth = d.date();
  const lastDayOfMonth = d.daysInMonth();

  const isFirstWeek = dayOfMonth <= 7;
  const isLastWeek = dayOfMonth > lastDayOfMonth - 7;

  return isFirstWeek || isLastWeek;
}

/**
 * Gets the number of days in a given month.
 *
 * @param date - Date object to get the month from
 * @returns Number of days in the month
 */
export function getDaysInMonth(date: Date): number {
  return dayjs(date).daysInMonth();
}

/**
 * Gets the nth occurrence of a weekday in a given month.
 *
 * @param year - The year
 * @param month - The month (0-indexed)
 * @param dayOfWeek - The day of week (0=Sunday, 6=Saturday)
 * @param nth - Which occurrence (1-5 for nth, -1 for last)
 * @returns The date of the nth occurrence, or null if it doesn't exist
 */
export function getNthWeekdayOfMonth(
  year: number,
  month: number,
  dayOfWeek: number,
  nth: number,
): Date | null {
  const firstOfMonth = dayjs(new Date(year, month, 1));
  const daysInMonth = firstOfMonth.daysInMonth();

  if (nth === -1) {
    // Find the last occurrence: start from end of month, find first matching weekday
    const lastOfMonth = firstOfMonth.date(daysInMonth);
    const lastDayOfWeek = lastOfMonth.day();
    const diff = (lastDayOfWeek - dayOfWeek + 7) % 7;
    return lastOfMonth.subtract(diff, 'day').toDate();
  }

  // Find the nth occurrence using math:
  // 1. Find the first occurrence of the weekday in the month
  const firstDayOfWeek = firstOfMonth.day();
  const daysUntilFirst = (dayOfWeek - firstDayOfWeek + 7) % 7;
  const firstOccurrence = 1 + daysUntilFirst;

  // 2. Calculate the nth occurrence
  const nthDay = firstOccurrence + (nth - 1) * 7;

  // 3. Check if it's within the month
  if (nthDay > daysInMonth) {
    return null;
  }

  return firstOfMonth.date(nthDay).toDate();
}

/** Milliseconds in a UTC day. */
const MS_PER_UTC_DAY = 86_400_000;

/**
 * Epoch milliseconds floored to the start of its UTC day. Useful for bucketing
 * events into UTC calendar days regardless of the viewer's timezone.
 *
 * @param date - Source date
 * @returns Epoch ms at 00:00:00 UTC of that day
 */
export function startOfUtcDayMs(date: Date): number {
  return Math.floor(date.getTime() / MS_PER_UTC_DAY) * MS_PER_UTC_DAY;
}

/**
 * Formats an epoch-ms timestamp as a UTC day key (YYYY-MM-DD).
 *
 * @param ms - Epoch milliseconds
 * @returns UTC day key, e.g. "2026-06-19"
 */
export function formatUtcDayKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Formats an epoch-ms timestamp as a full RFC-3339 UTC timestamp, for API
 * parameters that take an absolute instant (e.g. a metrics query's time range).
 *
 * @param ms - Epoch milliseconds
 * @returns RFC-3339 UTC timestamp, e.g. "2026-06-19T00:00:00.000Z"
 */
export function formatUtcTimestamp(ms: number): string {
  return new Date(ms).toISOString();
}

/**
 * Formats an epoch-ms instant in UTC using a dayjs format string. The UTC
 * counterpart of {@link formatEpochMs}, which formats in the viewer's own zone.
 *
 * @param epochMs - Milliseconds since epoch
 * @param format - Format string in dayjs format (default: "MMM D, YYYY")
 * @returns Formatted label, or an empty string if invalid
 *
 * @example
 * formatEpochMsUtc(Date.parse('2026-09-14T23:30:00Z'), 'MMM D') // "Sep 14"
 */
export function formatEpochMsUtc(
  epochMs: number,
  format: string = DATE_FORMAT_TOKENS.SHORT_DATE,
): string {
  const dayjsDate = dayjs.utc(epochMs);
  if (!dayjsDate.isValid()) {
    return '';
  }

  return dayjsDate.format(format);
}

/** A UTC day span, epoch ms at each end's midnight. BOTH ends inclusive. */
export interface IInclusiveUtcDays {
  /** First covered UTC day. */
  startMs: number;
  /** Last covered UTC day. */
  endMs: number;
}

/**
 * Epoch ms for a wire stamp read as UTC, or null when absent or unparseable.
 * The same `dayjs.utc` parse the UTC formatters use, so the validity check and
 * the rendered label can never disagree about what is readable.
 */
function parseUtcMs(timestamp: string | undefined): number | null {
  if (!timestamp) return null;
  const dayjsDate = dayjs.utc(timestamp);
  return dayjsDate.isValid() ? dayjsDate.valueOf() : null;
}

/**
 * A half-open interval as the UTC days it actually covers.
 *
 * `end_time` on a `google.type.Interval` is EXCLUSIVE, so the last covered day
 * is one UTC day before it. Subtracts a fixed UTC day rather than using
 * {@link addDays}, which shifts a Date in LOCAL time and would land on the wrong
 * day for any viewer off UTC.
 *
 * @param startTime - Inclusive start stamp (ISO 8601)
 * @param endTimeExclusive - Exclusive end stamp (ISO 8601)
 * @returns The covered days, or null when either bound is unreadable or the
 *   interval covers no day at all (`start == end` is empty; narrower is
 *   malformed)
 *
 * @example
 * toInclusiveUtcDays('2026-08-16T00:00:00Z', '2026-09-15T00:00:00Z')
 * // { startMs: <16 Aug 00:00Z>, endMs: <14 Sep 00:00Z> }
 * toInclusiveUtcDays('2026-08-16T00:00:00Z', '2026-08-16T00:00:00Z') // null
 */
export function toInclusiveUtcDays(
  startTime: string | undefined,
  endTimeExclusive: string | undefined,
): IInclusiveUtcDays | null {
  const startMs = parseUtcMs(startTime);
  const endExclusiveMs = parseUtcMs(endTimeExclusive);
  if (startMs === null || endExclusiveMs === null) return null;

  const endMs = endExclusiveMs - MS_PER_UTC_DAY;
  return endMs < startMs ? null : { startMs, endMs };
}

/**
 * Formats an epoch-ms timestamp as a short UTC day label (e.g. "Jun 19").
 *
 * @param ms - Epoch milliseconds
 * @returns Short month/day label in UTC
 */
export function formatUtcDayShort(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Formats a `YYYY-MM` period bucket as a short month label (e.g. "Jun 2026").
 * Falls back to the original string when it isn't a valid period.
 *
 * @param period - Period bucket, e.g. "2026-06"
 * @returns Human month label, or the input unchanged
 */
export function formatPeriod(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  const mi = m ? Number(m[2]) - 1 : -1;
  return m && MONTH_NAMES_SHORT[mi]
    ? `${MONTH_NAMES_SHORT[mi]} ${m[1]}`
    : period;
}

/**
 * Formats protocol calendar and clock fields using the requested locale.
 *
 * These are wall-clock fields, not a timestamp to convert to the browser's
 * timezone. Nanoseconds remain exact; timezone labels belong to the caller.
 *
 * @param value Protocol date, time or datetime fields.
 * @param locale Display locale; defaults to the browser locale.
 * @returns Localized fields, or undefined outside the supported date range.
 */
export function formatProtocolTemporal(
  value: {
    date?: TypeDate;
    timeofday?: TypeTimeOfDay;
    datetime?: TypeDateTime;
  },
  locale?: string,
): string | undefined {
  const calendar = value.datetime ?? value.date;
  const clock = value.datetime ?? value.timeofday;
  if (calendar && !calendar.year && !calendar.month && !calendar.day && !clock)
    return undefined;
  const date = new Date(0);
  date.setUTCFullYear(
    calendar?.year || 2000,
    (calendar?.month || 1) - 1,
    calendar?.day || 1,
  );
  date.setUTCHours(
    clock?.hours ?? 0,
    clock?.minutes ?? 0,
    clock?.seconds ?? 0,
    0,
  );
  if (!Number.isFinite(date.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    ...(calendar
      ? {
          ...(calendar.year ? ({ year: 'numeric' } as const) : {}),
          ...(calendar.month ? ({ month: 'short' } as const) : {}),
          ...(calendar.day ? ({ day: 'numeric' } as const) : {}),
        }
      : {}),
    ...(clock
      ? ({
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        } as const)
      : {}),
    ...(clock?.nanos ? ({ fractionalSecondDigits: 3 } as const) : {}),
  }).formatToParts(date);
  return parts
    .map(part =>
      part.type === 'fractionalSecond'
        ? new Intl.NumberFormat(locale, {
            useGrouping: false,
            minimumIntegerDigits: 9,
          }).format(clock?.nanos ?? 0)
        : part.value,
    )
    .join('');
}
